import { Button, Breadcrumb, Space, Table, Tag, Typography } from "antd";
import { EyeOutlined, HomeOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";
import { useContext, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import moment from "moment";
import { openNotification } from "../../../components/Notification";
import PageHeader from "../../../components/PageHeader";
import { SecaoCard } from "../../../components/SecaoCard";
import { ModalLeituras } from "../../../components/MensagensGlobais/ModalLeituras";
import { UserContext } from "../../../contexts/UserContext";
import { mensagemGlobalService } from "../../../services/resources/mensagemGlobalService";
import { conectarMensagensGlobais } from "../../../services/resources/mensagemGlobalSocket";
import "../../../components/MensagensGlobais/mensagemGlobal.css";
import {
  DATA_FORMATO,
  OPCOES_TAMANHO_PAGINA,
  podeGerenciarMensagensGlobais,
  urls,
} from "../../../utils";

function nomesGrupos(record) {
  return (record?.grupos || []).map((grupo) => grupo.nome).filter(Boolean);
}

function rotuloDestino(record) {
  if (record?.destino === "GRUPO") {
    const nomes = nomesGrupos(record);
    if (!nomes.length) return "grupo";
    if (nomes.length <= 2) return nomes.join(", ");
    return `${nomes.slice(0, 2).join(", ")} +${nomes.length - 2}`;
  }
  return "todos";
}

export const ListagemMensagemGlobal = () => {
  const history = useHistory();
  const { user } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState([]);
  const [mensagemLeituras, setMensagemLeituras] = useState(null);
  const [paginacao, setPaginacao] = useState({
    currentPage: 0,
    totalElements: 0,
    size: OPCOES_TAMANHO_PAGINA[0],
  });

  const autorizado = podeGerenciarMensagensGlobais(user);

  useEffect(() => {
    if (autorizado) {
      carregar(0, paginacao.size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autorizado]);

  useEffect(() => {
    if (!autorizado) return undefined;
    return conectarMensagensGlobais({
      onLeitura: (evento) => {
        if (!evento?.codigo) return;
        setDados((lista) =>
          lista.map((item) =>
            String(item.codigo) === String(evento.codigo)
              ? { ...item, quantidadeLeituras: evento.quantidadeLeituras ?? item.quantidadeLeituras }
              : item
          )
        );
      },
    });
  }, [autorizado]);

  async function carregar(page, size) {
    setLoading(true);
    try {
      const { data } = await mensagemGlobalService.listarAdmin({ page, size });
      setDados(data.content || []);
      setPaginacao({
        currentPage: data.currentPage,
        totalElements: data.totalElements,
        size: data.size,
      });
    } catch (error) {
      openNotification(
        "error",
        <strong>Erro ao listar mensagens</strong>,
        error?.response?.data?.userMessage || error?.response?.data?.detail
      );
    } finally {
      setLoading(false);
    }
  }

  const columns = [
    { title: <strong>título</strong>, dataIndex: "titulo", key: "titulo" },
    {
      title: <strong>enviada em</strong>,
      dataIndex: "dataCadastro",
      render: (value) => (value ? moment(value).format(`${DATA_FORMATO} HH:mm`) : "-"),
    },
    {
      title: <strong>autor</strong>,
      dataIndex: "criadoPorNome",
      render: (value) => value || "-",
    },
    {
      title: <strong>destino</strong>,
      key: "destino",
      render: (record) => (
        <Tag color={record.destino === "GRUPO" ? "orange" : "default"}>
          {rotuloDestino(record)}
        </Tag>
      ),
    },
    {
      title: <strong>leituras</strong>,
      dataIndex: "quantidadeLeituras",
      render: (value) => value ?? 0,
    },
    {
      title: <strong>ações</strong>,
      key: "acoes",
      width: 120,
      render: (record) => (
        <Space>
          <Button
            type="primary"
            ghost
            className="action-button"
            title="Visualizar"
            onClick={() =>
              history.push(
                urls.MENSAGENS_GLOBAIS_DETAILS.replace(":codigo", record.codigo)
              )
            }
          >
            <EyeOutlined />
          </Button>
          <Button
            type="primary"
            ghost
            className="action-button"
            title="Quem leu"
            onClick={() => setMensagemLeituras(record)}
          >
            <TeamOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  if (!autorizado) {
    return (
      <div id="sectionMain">
        <Tag color="red">Acesso restrito ao grupo OISOL MASTER</Tag>
      </div>
    );
  }

  return (
    <div id="sectionMain" className="pagina-mensagens-globais">
      <Breadcrumb id="breadcrumb">
        <Breadcrumb.Item>
          <Link to="">
            <HomeOutlined />
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>gerenciamento</Breadcrumb.Item>
        <Breadcrumb.Item>
          <strong>MENSAGENS GLOBAIS</strong>
        </Breadcrumb.Item>
      </Breadcrumb>
      <div className="container-section">
        <PageHeader
          titulo="MENSAGENS GLOBAIS"
          extra={[
            <Link to={urls.MENSAGENS_GLOBAIS_CRIAR} key="nova">
              <Button type="primary" icon={<PlusOutlined />}>
                nova mensagem
              </Button>
            </Link>,
          ]}
        />
        <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>
          Envie para todos os usuários já cadastrados ou somente para um ou mais grupos.
          Quem entrar no sistema depois do disparo não receberá a mensagem.
        </Typography.Paragraph>
        <SecaoCard>
        <Table
          rowKey="codigo"
          loading={loading}
          columns={columns}
          dataSource={dados}
          pagination={{
            size: "small",
            current: paginacao.currentPage + 1,
            pageSize: paginacao.size,
            total: paginacao.totalElements,
            showSizeChanger: true,
            pageSizeOptions: OPCOES_TAMANHO_PAGINA,
            showTotal: (total) => <strong>{total} registros no total</strong>,
          }}
          onChange={(pagination) =>
            carregar(pagination.current - 1, pagination.pageSize)
          }
        />
        </SecaoCard>
      </div>
      <ModalLeituras
        visivel={Boolean(mensagemLeituras)}
        mensagem={mensagemLeituras}
        onFechar={() => setMensagemLeituras(null)}
      />
    </div>
  );
};
